import { createClient, type SupabaseClient } from '@supabase/supabase-js';
import { captureConfigError } from './sentry';

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

/**
 * Whether the Supabase backend is configured for this build/runtime.
 *
 * When false, the app boots in a degraded mode: the builder, guides and
 * calculators work fully (they are backend-free), while auth, save/load and
 * live quotes are switched off with a clear notice instead of taking down the
 * whole app. See docs/issues/102-supabase-env-degraded-mode.md.
 *
 * Missing env is treated as a misconfiguration, not a supported deploy mode —
 * hence the loud, once-only init error below (dev console + Sentry). A typo in
 * a production deploy env must not degrade silently.
 */
export const isSupabaseConfigured = Boolean(supabaseUrl && supabaseAnonKey);

if (!isSupabaseConfigured) {
    // Log once at module init — this file is imported once, so no de-dup guard
    // is needed. Callers must additionally handle a null client at runtime.
    captureConfigError(
        'Supabase is not configured: missing VITE_SUPABASE_URL or ' +
            'VITE_SUPABASE_ANON_KEY. Auth, save/load and live quotes are ' +
            'disabled; the builder still works in a local-only degraded mode.',
    );
}

export { supabaseUrl };

/**
 * The Supabase client, or `null` when unconfigured. Every consumer must handle
 * the null case (guard, or the degraded `useAuth`/repo/quote helpers do it for
 * you) rather than assuming a live client.
 */
export const supabase: SupabaseClient | null = isSupabaseConfigured
    ? createClient(supabaseUrl as string, supabaseAnonKey as string)
    : null;
